"use client";

import { useState, useEffect } from "react";
import * as Yup from "yup";
import { Button, TextField, Box, Typography } from "@mui/material";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { useFormik } from "formik";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { useAlertStore } from "@/components/context/alert-store";
import { useCheckoutStore } from "@/components/context/checkout-store";
import Cookies from "js-cookie";
import { postRequest } from "@/utils/request";
import { magicConstants } from "@/utils/magic-constants";
import { encryptCredentials } from "@/utils/credentials-storage";
import { credentialSignIn } from "@/utils/credential-sign-in";
import Link from "@/components/core/localized-mui-link";
import useLocalizedRouter from "@/hooks/use-localized-router";
import {
  GUEST_CART_MERGE_KEY,
  GUEST_LOGIN_TRANSITION_COOKIE,
} from "@/components/core/session-monitor";

const ERROR_COLOR = "#fd7b7c";

const SEVERE_ERRORS = new Set([
  "TOO_MANY_ATTEMPTS",
  "An unexpected error occurred",
  "An unexpected error occurred. Please try again later.",
]);

function getRawErrorMessage(error: unknown): string {
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message;
  return "";
}

function getAlertSeverity(error: unknown): "error" | "warning" {
  return SEVERE_ERRORS.has(getRawErrorMessage(error)) ? "error" : "warning";
}

export default function LoginForm({ isCheckout }: Readonly<{ isCheckout?: boolean }>) {
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingGuest, setLoadingGuest] = useState<boolean>(false);

  const { setMessage, showUnexpectedErrorMessage } = useAlertStore();
  const router = useLocalizedRouter();
  const { setCheckoutOtp } = useCheckoutStore();
  const queryClient = useQueryClient();

  useEffect(() => {
    localStorage.removeItem("login-credentials");
    localStorage.removeItem("otp-expiry");
  }, []);

  const getErrorMessage = (error: unknown) => {
    const raw = getRawErrorMessage(error);
    if (raw === "TOO_MANY_ATTEMPTS") {
      return t`Too many attempts, please try again later`;
    }
    return raw || t`An unexpected error occurred. Please try again later.`;
  };

  const showLoginError = (error: unknown) => {
    setMessage({ text: getErrorMessage(error), severity: getAlertSeverity(error) });
  };

  const invalidateAuthQueries = () => {
    queryClient.invalidateQueries({ queryKey: ["payment-addresses"] });
    queryClient.invalidateQueries({ queryKey: ["cart"] });
  };

  const handleOtpRequired = async (error: string, values: { email: string; password: string }) => {
    const expiryMatch = error.match(/^OTP_REQUIRED:(.+)$/);
    if (expiryMatch?.[1]) {
      localStorage.setItem("otp-expiry", expiryMatch[1]);
    }

    localStorage.setItem("current-email", values.email);
    const encrypted = await encryptCredentials({
      email: values.email,
      password: values.password,
    });
    localStorage.setItem("login-credentials", encrypted);

    if (isCheckout) {
      setCheckoutOtp(true);
    } else {
      router.push("/register/otp");
    }
  };

  const handleLogin = async (values: { email: string; password: string }) => {
    setLoading(true);
    setMessage(null);
    const shouldMergeGuestCart = isCheckout && localStorage.getItem(GUEST_CART_MERGE_KEY) === "1";

    try {
      const result = await credentialSignIn({
        email: values.email,
        password: values.password,
        action: "login",
        ...(shouldMergeGuestCart ? { is_guest: "1", guest_transition: "1" } : {}),
      });

      invalidateAuthQueries();

      if (!result?.error) {
        if (shouldMergeGuestCart) {
          localStorage.removeItem(GUEST_CART_MERGE_KEY);
          Cookies.remove(GUEST_LOGIN_TRANSITION_COOKIE, { path: "/" });
        }
        return;
      }

      if (result.error.startsWith("OTP_REQUIRED:")) {
        await handleOtpRequired(result.error, values);
        return;
      }

      showLoginError(result.error);
    } catch (error) {
      showLoginError(error);
    } finally {
      setLoading(false);
    }
  };

  const guestMutation = useMutation({
    mutationFn: () => postRequest({ path: { name: "registerGuest" }, body: {} }),
    onSuccess: async (data: any) => {
      if (data?.success === 0) {
        setMessage({ text: t`Guest registration failed.`, severity: "warning" });
        setLoadingGuest(false);
        return;
      }

      try {
        const result = await credentialSignIn({
          email: data?.data.email,
          password: magicConstants.guestLegit,
          action: "login",
          is_guest: "1",
        });

        invalidateAuthQueries();

        if (result?.error) {
          showLoginError(result.error);
          return;
        }

        setMessage({ text: t`Signed in as guest.`, severity: "success" });
      } catch {
        showUnexpectedErrorMessage();
      } finally {
        setLoadingGuest(false);
      }
    },
    onError: () => {
      setLoadingGuest(false);
      setMessage({
        text: t`An unexpected error occurred during guest registration. Please try again later.`,
        severity: "error",
      });
    },
  });

  const formik = useFormik({
    initialValues: { email: "", password: "" },
    validationSchema: Yup.object({
      email: Yup.string()
        .email(t`Invalid email address`)
        .required(t`Email is required`),
      password: Yup.string().required(t`Password is required`),
    }),
    onSubmit: handleLogin,
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <Box sx={{ mb: "20px" }}>
        <TextField
          data-testid="login-email"
          id="email"
          name="email"
          placeholder={t`E-Mail`}
          type="email"
          value={formik.values.email}
          onChange={formik.handleChange}
        />
        {formik.touched.email && formik.errors.email && (
          <Typography sx={{ pt: "4px" }} variant="caption" color={ERROR_COLOR}>
            {formik.errors.email}
          </Typography>
        )}
      </Box>

      <Box sx={{ mb: "20px" }}>
        <TextField
          data-testid="login-password"
          id="password"
          name="password"
          placeholder={t`Password`}
          type="password"
          value={formik.values.password}
          onChange={formik.handleChange}
        />
        {formik.touched.password && formik.errors.password && (
          <Typography sx={{ pt: "4px" }} variant="caption" color={ERROR_COLOR}>
            {formik.errors.password}
          </Typography>
        )}
      </Box>

      <Box>
        <Link
          data-testid="forgot-password-link"
          href="/forgot-password"
          underline="always"
          color="primary.main"
          sx={{
            fontSize: "14px",
            "&:hover": { color: "#333", transition: "0.3s ease" },
          }}
        >
          <Trans>Forgotten password?</Trans>
        </Link>
      </Box>

      <div className="flex flex-col gap-5 pt-7">
        <Button
          data-testid="login-btn"
          type="submit"
          color="primary"
          fullWidth
          variant="contained"
          disabled={loading}
          sx={{
            "&.Mui-disabled": {
              backgroundColor: "primary.main",
              color: "white",
              opacity: 1,
              cursor: "not-allowed",
            },
          }}
        >
          <Trans>Login</Trans>
          <Typography component="span" className={loading ? "loading" : ""} />
        </Button>

        {isCheckout && (
          <Button
            data-testid="guest-login-btn"
            type="button"
            color="primary"
            fullWidth
            variant="contained"
            disabled={loadingGuest}
            onClick={() => {
              setLoadingGuest(true);
              guestMutation.mutate();
            }}
            sx={{
              borderRadius: "6px",
              border: "1px solid #e5f7fa",
              backgroundColor: "#e5f7fa",
              color: "#007e8a",
              textTransform: "none",
              fontWeight: 500,
              "&:hover": {
                backgroundColor: "#d6f1f6",
                borderColor: "#e5f7fa",
                color: "#007e8a",
              },
              ...(loadingGuest && {
                opacity: 0.7,
                cursor: "wait",
                pointerEvents: "none",
              }),
            }}
          >
            <Trans>Continue as guest</Trans>
            <Typography component="span" className={loadingGuest ? "loading" : ""} />
          </Button>
        )}
      </div>
    </form>
  );
}
